import Vue from 'vue'
import momentTimezone from 'moment-timezone'
import matchService from '@/services/match'

// Fallback for matches without stat logs (no stats operator): live from tip-off until this many minutes later
export const LIVE_WINDOW_MINUTES = 120

// Matches whose start falls in this range around "now" get their logs checked
const CHECK_BEFORE_START_MINUTES = 15
export const CHECK_AFTER_START_MINUTES = 240
const STATUS_TTL_MS = 60 * 1000
// A live match gets its logs (state and score) checked this often
const LIVE_STATUS_TTL_MS = 15 * 1000
// How often the pages re-evaluate "now" and the live statuses, so a match turns live and its score moves without a reload
export const LIVE_TICK_MS = 15 * 1000
// How often the pages reload their match lists in the background (new times, postponements, final results)
export const SCHEDULE_REFRESH_MS = 60 * 1000

const MINUTE = 60 * 1000

// statuses[matchId] = { state: 'live' | 'ended' | 'unknown', points: { [teamId]: points } | null, fetchedAt }
const store = Vue.observable({ statuses: {} })
const inFlight = {}

function getStart(match) {
    return momentTimezone(match.matchDatetime).valueOf()
}

function shouldCheck(match, now) {
    if (!match || !match.id || match.postponed || !match.matchDatetime) {
        return false
    }
    const start = getStart(match)
    return now >= start - CHECK_BEFORE_START_MINUTES * MINUTE && now < start + CHECK_AFTER_START_MINUTES * MINUTE
}

// "End game" in the CMS writes one last log flagged `matchEnded`
export function hasMatchEnded(logs = []) {
    return (Array.isArray(logs) ? logs : []).some(log => log && log.matchEnded === true)
}

// The flag decides when it is there. Matches played before it existed: the stats app logs `begin`
// when a player checks in and `stop` when he checks out, and "end game" stops everyone still on court
export function getStateFromLogs(logs = []) {
    if (hasMatchEnded(logs)) {
        return 'ended'
    }
    const balance = {}
    let hasBegin = false

    logs.forEach(log => {
        if (log.playerMatchStatus === 'begin') {
            hasBegin = true
            balance[log.playerId] = (balance[log.playerId] || 0) + 1
        } else if (log.playerMatchStatus === 'stop') {
            balance[log.playerId] = (balance[log.playerId] || 0) - 1
        }
    })

    if (!hasBegin) {
        return 'unknown'
    }
    return Object.values(balance).some(value => value > 0) ? 'live' : 'ended'
}

// Points per team from the stat logs, same formula as the backend's MatchStat.points
export function getPointsFromLogs(logs = []) {
    const points = {}
    let hasTeam = false
    ;(Array.isArray(logs) ? logs : []).forEach(log => {
        if (!log || log.teamId === null || log.teamId === undefined) return
        hasTeam = true
        const made = (Number(log.twoPointsMade) || 0) * 2 + (Number(log.threePointsMade) || 0) * 3 + (Number(log.freeThrowsMade) || 0)
        points[log.teamId] = (points[log.teamId] || 0) + made
    })
    return hasTeam ? points : null
}

export function refreshLiveStatuses(matches = [], now = Date.now()) {
    matches.filter(match => shouldCheck(match, now)).forEach(match => {
        const cached = store.statuses[match.id]
        const ttl = cached && cached.state === 'live' ? LIVE_STATUS_TTL_MS : STATUS_TTL_MS
        if (inFlight[match.id] || (cached && now - cached.fetchedAt < ttl)) {
            return
        }
        inFlight[match.id] = matchService.getMatchLogs(match.id).then(resp => {
            Vue.set(store.statuses, match.id, { state: getStateFromLogs(resp.data), points: getPointsFromLogs(resp.data), fetchedAt: Date.now() })
        }).catch(() => {
            Vue.set(store.statuses, match.id, { state: 'unknown', points: null, fetchedAt: Date.now() })
        }).finally(() => {
            delete inFlight[match.id]
        })
    })
}

export function isMatchLive(match, now = Date.now()) {
    if (!shouldCheck(match, now)) {
        return false
    }

    const cached = store.statuses[match.id]
    if (cached && cached.state === 'live') {
        return true
    }
    if (cached && cached.state === 'ended') {
        return false
    }

    const start = getStart(match)
    return now >= start && now < start + LIVE_WINDOW_MINUTES * MINUTE
}

// The match as the lists should show it right now: while live, the score of the stat logs; once "end game"
// is in the logs, a result, even if the list was loaded before the backend stopped marking it pending
export function withLiveData(match, now = Date.now()) {
    if (!shouldCheck(match, now)) {
        return match
    }
    const cached = store.statuses[match.id]
    const live = isMatchLive(match, now)
    const ended = !!cached && cached.state === 'ended'
    if (!live && !ended) {
        return match
    }

    const points = cached && cached.points
    const team1Id = match.team1 && match.team1.id
    const team2Id = match.team2 && match.team2.id
    const hasLogScore = !!points && (points[team1Id] !== undefined || points[team2Id] !== undefined)
    // the list score once the backend has it (it can include CMS corrections), the logs until then
    const useLogScore = hasLogScore && (live || match.pending)
    return {
        ...match,
        pending: live ? match.pending : false,
        scoreTeam1: useLogScore ? (points[team1Id] || 0) : match.scoreTeam1,
        scoreTeam2: useLogScore ? (points[team2Id] || 0) : match.scoreTeam2
    }
}

// Runs fn every ms while the page is visible, and right away when it becomes visible again. Returns the stop function
export function pollWhileVisible(fn, ms) {
    const run = () => {
        if (!document.hidden) fn()
    }
    const timer = setInterval(run, ms)
    const onVisible = () => {
        if (!document.hidden) fn()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
        clearInterval(timer)
        document.removeEventListener('visibilitychange', onVisible)
    }
}

// Where the clock stands by the play-by-play (its game time, "mm:ss" from tip-off): 4 periods of 10 minutes,
// then overtimes of 5, as the stats app counts them. Null before the first log
export function getClockFromLogs(logs = []) {
    const seconds = (Array.isArray(logs) ? logs : []).reduce((latest, log) => {
        const [min = '0', sec = '0'] = String((log && log.timestamp) || '').split(':')
        return Math.max(latest, (parseInt(min, 10) || 0) * 60 + (parseInt(sec, 10) || 0))
    }, -1)
    if (seconds < 0) return null
    const period = seconds < 40 * 60 ? Math.floor(seconds / 600) + 1 : Math.floor((seconds - 40 * 60) / 300) + 5
    const start = period <= 4 ? (period - 1) * 600 : 40 * 60 + (period - 5) * 300
    const length = period <= 4 ? 600 : 300
    return { period, timer: Math.max(0, length - (seconds - start)) * 1000 }
}
